import { Patient } from "@cur8/rich-entity";
import { generateDocumentUrl, LegalDocument } from "lib/legal";
import { useCallback, useMemo } from "react";
import { useConfig } from "render/context/ConfigContext";
import { useGiveStudyConsentMutation } from "render/hooks/mutations/consent/useGiveStudyConsentMutation";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader/LogoHeader";
import { ActionButton } from "render/ui/trigger/ActionButton/ActionButton";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface AgreeToAutomaticSummariesViewProps {
  document: string;
  patient: Patient;
  onSkip: () => void;
  onSuccess: () => void;
}

export function AgreeToAutomaticSummariesView({
  document,
  onSkip,
  onSuccess,
}: AgreeToAutomaticSummariesViewProps) {
  const { appConfig } = useConfig();
  const giveStudyConsentMutation = useGiveStudyConsentMutation();
  const documentUrl = generateDocumentUrl(
    appConfig.legalApiUrl,
    LegalDocument.AutomaticSummary,
    "sv"
  );
  const documentHtml = useMemo(() => {
    const parser = new DOMParser();
    return parser.parseFromString(document, "text/html");
  }, [document]);
  const head = useMemo(() => {
    return documentHtml.getElementById("summary");
  }, [documentHtml]);
  const body = useMemo(() => {
    const content = documentHtml.getElementById("scrollable");
    if (content) {
      Array.from(content.getElementsByTagName("h2")).forEach(
        (header, index) => {
          const text = header.innerText;
          const number = index + 1;
          header.innerHTML = `<div class=${styles.numerator}><div>${number
            .toString()
            .padStart(2, "0")}</div><div class=${
            styles.line
          }></div></div> ${text}`;
        }
      );
    }

    return content;
  }, [documentHtml]);
  const onClick = useCallback(async () => {
    await giveStudyConsentMutation({
      documentUri: documentUrl,
      consentSignature: {
        $type: "login",
      },
    });
    onSuccess();
  }, [giveStudyConsentMutation, documentUrl, onSuccess]);

  return (
    <FullScreenPageLayout>
      <div className={styles.AgreeToAutomaticSummariesView}>
        <div className={styles.sticky}>
          <LogoHeader />
        </div>
        {head && (
          <div
            className={styles.header}
            dangerouslySetInnerHTML={{ __html: head.innerHTML }}
          ></div>
        )}
        {body && (
          <div
            className={styles.body}
            dangerouslySetInnerHTML={{ __html: body.innerHTML }}
          ></div>
        )}
        <div className={styles.cta}>
          <div className={styles.actions}>
            <ActionButton onClick={onClick}>
              <Trans.Agree />
            </ActionButton>
            <ActionButton variant="secondary" onClick={onSkip}>
              <Trans.NextTime />
            </ActionButton>
          </div>
        </div>
      </div>
    </FullScreenPageLayout>
  );
}
